// Batch 0 (UX/IA overhaul) — the shared pagination control.
//
// Drives its window from the pure paginate() math and emits page changes.
// Works for both client-side lists and server lists (the parent maps
// PageInfo.offset onto the `?offset=` the backends already accept). Renders a
// "41–60 of 95" summary so an operator always knows a list is truncated —
// the fix for the silent 50/100/200 caps the audit flagged.
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { paginate } from "@/lib/pagination";
import { cn } from "@/lib/utils";

export interface PaginationProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function Pagination({
  total,
  page,
  pageSize,
  onPageChange,
  className,
}: PaginationProps) {
  const info = paginate(total, page, pageSize);
  if (info.total === 0) return null;
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 px-4 py-3 text-xs text-muted-foreground",
        className,
      )}
    >
      <span aria-live="polite">
        {info.from}–{info.to} of {info.total}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!info.hasPrev}
          onClick={() => onPageChange(info.page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        <span className="tabular-nums">
          Page {info.page} / {info.pageCount}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={!info.hasNext}
          onClick={() => onPageChange(info.page + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
